import { Box, Button } from "@mui/material";
import { useFormik } from "formik";
import { useState } from "react";
import {
  VIEW_EDIT_PROFILE_VALIDATION_SCHEMA,
  VIEW_PROFILE_INITIAL_VALUES,
} from "../../../../constants/initial-schemas";
import { RoleCols } from "../../../../dummy-data/provider-roles-and-resp";
import AsyncSelect from "../../../AsyncSelect";
import CustomTextArea from "../../../CustomTextArea";
import Flex from "../../../Flex";
import Input from "../../../Input";
import SideSheetDrawer from "../../../SidesheetDrawer";
import NewRoleTable from "./NewRoleTable";
import { useAddRole } from "../../../../queries/tenant-setting/use-add-role";
import { useSnackbar } from "../../../../hooks/useSnackbar";
import { useQueryClient } from "@tanstack/react-query";
import { showToastOnApiCall } from "../../../../utils/utils";
import { useUpdateRole } from "../../../../queries/tenant-setting/use-update-role";
import { SELECT_ROLE_OPTIONS } from "./rolesResponsibility";
export type Action = "view" | "add" | "update" | "copy";

interface UpsertRoleModalProps {
  titleDetail?: Partial<RoleCols>;
  opened: boolean;
  onClose: () => void;
  action: Action;
  onEditClick?: () => void;
  onSave?: (v: any) => Promise<any>;
  prefillValue?: any;
}

const UpsertRoleModal = ({
  opened,
  onClose,
  action,
  // onEditClick,
  prefillValue,
}: UpsertRoleModalProps) => {
  const showSnackbar = useSnackbar();
  const [roleId, setRoleId] = useState<number>(prefillValue?.id);
  const { mutateAsync: addNewRole } = useAddRole();
  const { mutateAsync: updateRole } = useUpdateRole();

  const queryClient = useQueryClient();
  const formik = useFormik({
    initialValues: prefillValue || VIEW_PROFILE_INITIAL_VALUES,
    validationSchema: VIEW_EDIT_PROFILE_VALIDATION_SCHEMA,
    enableReinitialize: true,
    onSubmit: (v) => {
      setIsRoleSaved(true);
      const payload = {
        role: {
          ...v,
          type: v.type?.value,
        },
      };
      if (prefillValue) {
        showToastAndResetForm(updateRole(payload));
      } else {
        showToastAndResetForm(addNewRole(payload));
      }
    },
  });

  async function showToastAndResetForm(apiCall: Promise<any>) {
    showToastOnApiCall({
      apiCall,
      showSnackbar,
      onSave: (res: any) => {
        if (!prefillValue) setRoleId(res?.data?.data?.id);
      },
    });
  }

  const [isRoleSaved, setIsRoleSaved] = useState<boolean>(false);
  const title = MODAL_TITLE_MAPPER[action];

  const closeAndReset = () => {
    setIsRoleSaved(false);
    formik.resetForm();
    queryClient.invalidateQueries({
      queryKey: ["roles-and-responsibilities-list"],
    });
    onClose();
  };

  return (
    <SideSheetDrawer
      open={opened}
      onClose={closeAndReset}
      title={title}
      w="50vw"
      footerActions={
        !isRoleSaved && action !== "view" ? (
          <>
            <Button variant="contained" onClick={formik.submitForm}>
              Save
            </Button>
            <Button
              variant="contained"
              disabled={!prefillValue}
              onClick={() => {
                setIsRoleSaved(true);
                action = "view";
              }}
            >
              Skip
            </Button>
          </>
        ) : undefined
      }
    >
      <Box width="100%" height={"60vh"}>
        {!isRoleSaved && action !== "view" ? (
          <Flex flexDirection={"column"} gap={3.4}>
            <Input
              required
              label="Role Name"
              textFieldProps={{
                name: "name",
                value: formik.values?.name,
                onChange: formik.handleChange,
                onBlur: formik.handleBlur,
                placeholder: "Enter Role Name",
              }}
              error={formik.touched?.name && (formik.errors?.name as string)}
            />

            <AsyncSelect
              required
              value={formik.values?.type}
              placeholder="Search & Select Base Role"
              options={SELECT_ROLE_OPTIONS.admin}
              onChange={(val) => {
                formik.setFieldValue("type", val);
              }}
              error={formik.touched?.type && (formik.errors?.type as any)}
            />

            <CustomTextArea
              label={"Description"}
              name="description"
              minRows={4}
              value={formik.values?.description}
              onChange={formik.handleChange}
              placeholder="Enter Description"
            />
          </Flex>
        ) : (
          <NewRoleTable roleId={roleId} viewOnly={action === "view"} />
        )}
      </Box>
    </SideSheetDrawer>
  );
};

export default UpsertRoleModal;

const MODAL_TITLE_MAPPER: Record<Action, string> = {
  view: "View Role",
  add: "Add New Role",
  update: "Update Role",
  copy: "Make a Copy",
};
